import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Package, MapPin, TrendingUp, TrendingDown, Search, X } from 'lucide-react';
import type {
  MerchandiseMovementFormData,
  MerchandiseMovementType,
  MovementDirection
} from '@/types/merchandise-movement.types';
import type { Ingredient } from '@/types/ingredient.types';
import type { Location } from '@/types/location.types';
import { useIngredients } from '@/pages/inventory/hooks/useIngredients';
import { TextKeypad, DecimalKeypad } from '@/components/ui/keypads';
import { useAuth } from '@/context/AuthContext';
import {
  getEntryTypeLabel,
  getExitTypeLabel,
  getMovementTypeColor,
  requiresApproval,
  APPROVAL_THRESHOLD_AMOUNT,
  APPROVAL_THRESHOLD_PERCENTAGE
} from '@/types/merchandise-movement.types';

interface MerchandiseMovementFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MerchandiseMovementFormData) => void;
  defaultDirection?: MovementDirection;
  ingredients?: Ingredient[];
  locations?: Location[];
  entryTypes?: Array<{ id: string; name: string; direction: 'entry'; requiresApproval: boolean }>;
  exitTypes?: Array<{ id: string; name: string; direction: 'exit'; requiresApproval: boolean }>;
}


export const MerchandiseMovementFormModal: React.FC<MerchandiseMovementFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  defaultDirection = 'exit',
  ingredients: propIngredients,
  locations = [],
  entryTypes = [],
  exitTypes = []
}) => {
  const { ingredients: apiIngredients, loading: ingredientsLoading } = useIngredients();
  const { user, selectedLocation } = useAuth();
  const ingredients = propIngredients || apiIngredients;

  // Verificar si el usuario es CAJERO (positionId = 3)
  const isCajero = user?.positionId === 3;

  // Obtener el locationId del cajero
  const getCajeroLocationId = (): string => {
    if (!isCajero) return '';
    // Primero intentar con selectedLocation, luego con user.locationId
    if (selectedLocation?.id) return selectedLocation.id.toString();
    if (user?.locationId) return user.locationId.toString();
    return '';
  };

  const getDefaultMovementType = () => {
    if (defaultDirection === 'entry') {
      return entryTypes.length > 0 ? entryTypes[0].id : 'purchase';
    } else {
      return exitTypes.length > 0 ? exitTypes[0].id : 'waste';
    }
  };

  const [formData, setFormData] = useState<MerchandiseMovementFormData>({
    direction: defaultDirection,
    ingredientId: '',
    locationId: isCajero ? getCajeroLocationId() : '',
    quantity: 0,
    movementType: getDefaultMovementType(),
    reason: '',
    description: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [willRequireApproval, setWillRequireApproval] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
  const [quantityKeypadOpen, setQuantityKeypadOpen] = useState(false);
  const [reasonKeypadOpen, setReasonKeypadOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Cuando el modal se abre, setear locationId para CAJERO
      if (isCajero) {
        const cajeroLocationId = getCajeroLocationId();
        if (cajeroLocationId && formData.locationId !== cajeroLocationId) {
          setFormData(prev => ({ ...prev, locationId: cajeroLocationId }));
        }
      }
    } else {
      // Reset form when modal closes
      setFormData({
        direction: defaultDirection,
        ingredientId: '',
        locationId: isCajero ? getCajeroLocationId() : '',
        quantity: 0,
        movementType: getDefaultMovementType(),
        reason: '',
        description: ''
      });
      setErrors({});
      setSelectedIngredient(null);
      setWillRequireApproval(false);
      setSearchTerm('');
      setShowDropdown(false);
      setSearchKeypadOpen(false);
      setQuantityKeypadOpen(false);
      setReasonKeypadOpen(false);
    }
  }, [isOpen, defaultDirection, entryTypes, exitTypes, isCajero, selectedLocation]);

  useEffect(() => {
    // Update movement type when direction changes
    setFormData(prev => ({
      ...prev,
      direction: defaultDirection,
      movementType: getDefaultMovementType()
    }));
  }, [defaultDirection, entryTypes, exitTypes]);

  useEffect(() => {
    // Update selected ingredient when ingredientId changes
    if (formData.ingredientId) {
      const ingredient = ingredients.find(ing => ing.id === formData.ingredientId);
      setSelectedIngredient(ingredient || null);
    } else {
      setSelectedIngredient(null);
    }
  }, [formData.ingredientId, ingredients]);

  useEffect(() => {
    // Check if this movement will require approval
    if (selectedIngredient && formData.quantity > 0 && formData.locationId) {
      const locationStock = selectedIngredient.locationStocks?.find(
        stock => stock.locationId === formData.locationId
      );
      
      if (locationStock) {
        const unitCost = locationStock.costPerUnit || selectedIngredient.globalCostPerUnit || 0;
        const currentStock = locationStock.currentStock || 0;
        
        const needsApproval = requiresApproval(
          formData.direction,
          formData.movementType,
          formData.quantity,
          unitCost,
          currentStock
        );
        
        setWillRequireApproval(needsApproval);
      }
    } else {
      setWillRequireApproval(false);
    }
  }, [selectedIngredient, formData.quantity, formData.movementType, formData.locationId, formData.direction]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target && !target.closest('.ingredient-search-container')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.ingredientId) {
      newErrors.ingredientId = 'Debe seleccionar un insumo';
    }

    if (!formData.locationId) {
      newErrors.locationId = 'Debe seleccionar una sucursal';
    }

    if (formData.quantity <= 0) {
      newErrors.quantity = 'La cantidad debe ser mayor a 0';
    }

    // Check if quantity exceeds available stock (only for exits)
    if (formData.direction === 'exit' && selectedIngredient && formData.locationId && formData.quantity > 0) {
      const locationStock = selectedIngredient.locationStocks?.find(
        stock => stock.locationId === formData.locationId
      );
      
      if (locationStock && formData.quantity > locationStock.currentStock) {
        newErrors.quantity = `Stock insuficiente. Disponible: ${locationStock.currentStock} ${selectedIngredient.unit}`;
      }
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
      onClose();
    }
  };


  const getMovementTypeOptions = () => {
    return formData.direction === 'entry' ? entryTypes : exitTypes;
  };

  const getModalTitle = () => {
    return formData.direction === 'entry' ? 'Registrar Ingreso de Mercadería' : 'Registrar Salida de Mercadería';
  };

  const getModalIcon = () => {
    return formData.direction === 'entry' ? TrendingUp : TrendingDown;
  };

  // Para CAJERO: filtrar por listDestination LOCAL o AMBOS
  const cajeroIngredients = isCajero
    ? ingredients.filter(ing => {
        const dest = ing.listDestination;
        return !dest || dest === 'LOCAL' || dest === 'AMBOS';
      })
    : ingredients;

  // Filter ingredients based on search term (name or category)
  const filteredIngredients = cajeroIngredients.filter(ingredient => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return ingredient.name.toLowerCase().includes(term) ||
      (ingredient.category && ingredient.category.toLowerCase().includes(term));
  });

  // Para CAJERO: mostrar todos siempre. Para otros: solo si hay búsqueda
  const displayedIngredients = isCajero ? filteredIngredients : (searchTerm ? filteredIngredients.slice(0, 50) : []);

  const handleIngredientSelect = (ingredient: Ingredient) => {
    setFormData({...formData, ingredientId: ingredient.id});
    setSearchTerm(ingredient.name);
    setShowDropdown(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setShowDropdown(value.length > 0);

    // Clear selection if search doesn't match selected ingredient
    if (selectedIngredient && !selectedIngredient.name.toLowerCase().includes(value.toLowerCase())) {
      setFormData({...formData, ingredientId: ''});
    }
  };

  const clearIngredientSelection = () => {
    setFormData({...formData, ingredientId: ''});
    setSearchTerm('');
    setShowDropdown(false);
  };

  const openSearchKeypad = () => {
    setSearchKeypadOpen(true);
  };

  const handleSearchKeypadSubmit = (value: string) => {
    handleSearchChange(value);
  };

  const handleQuantityKeypadSubmit = (value: string) => {
    setFormData({...formData, quantity: parseFloat(value) || 0});
  };

  const handleReasonKeypadSubmit = (value: string) => {
    setFormData({...formData, reason: value});
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            {React.createElement(getModalIcon(), { className: "w-5 h-5 mr-2" })}
            {getModalTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Direction Selection - Hidden but value maintained */}
          {false && (
          <div className="space-y-2">
            <Label>Tipo de Movimiento</Label>
            <Select
              value={formData.direction}
              onValueChange={(value: MovementDirection) => {
                const newMovementType = value === 'entry'
                  ? (entryTypes.length > 0 ? entryTypes[0].id : 'purchase')
                  : (exitTypes.length > 0 ? exitTypes[0].id : 'waste');
                setFormData({
                  ...formData,
                  direction: value,
                  movementType: newMovementType
                });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="entry">
                  <div className="flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
                    Ingreso
                  </div>
                </SelectItem>
                <SelectItem value="exit">
                  <div className="flex items-center">
                    <TrendingDown className="w-4 h-4 mr-2 text-red-600" />
                    Salida
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          )}

          {/* Product Search */}
          <div className="space-y-2">
            <Label htmlFor="ingredient">Insumo *</Label>

            {isCajero ? (
              /* CAJERO: lista visible con filtro de texto arriba */
              <div>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    type="text"
                    value={searchTerm}
                    readOnly
                    onClick={() => setSearchKeypadOpen(true)}
                    placeholder="Filtrar insumos..."
                    className={`pl-10 cursor-pointer bg-white hover:bg-gray-50 ${errors.ingredientId ? 'border-red-500' : ''}`}
                  />
                  {searchTerm && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setSearchTerm(''); }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-100"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <div className="border rounded-md max-h-52 overflow-y-auto">
                  {displayedIngredients.length > 0 ? (
                    displayedIngredients.map(ingredient => (
                      <button
                        key={ingredient.id}
                        type="button"
                        onClick={() => handleIngredientSelect(ingredient)}
                        className={`w-full px-3 py-2 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                          formData.ingredientId === ingredient.id
                            ? 'bg-blue-100 border-l-4 border-l-blue-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-sm ${formData.ingredientId === ingredient.id ? 'font-bold text-blue-900' : 'font-medium text-gray-900'}`}>
                            {ingredient.name}
                          </span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {ingredient.unit}
                          </Badge>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No se encontraron insumos
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Otros roles: búsqueda con dropdown */
              <div className="relative ingredient-search-container">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Buscar insumo..."
                    className={`pl-10 pr-10 ${errors.ingredientId ? 'border-red-500' : ''}`}
                  />
                  {selectedIngredient && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearIngredientSelection}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-100"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                {showDropdown && displayedIngredients.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {displayedIngredients.map(ingredient => (
                      <button
                        key={ingredient.id}
                        type="button"
                        onClick={() => handleIngredientSelect(ingredient)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:bg-gray-50 focus:outline-none"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{ingredient.name}</span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {ingredient.unit}
                          </Badge>
                        </div>
                        {ingredient.category && (
                          <div className="text-xs text-gray-500 mt-1">
                            {ingredient.category}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {showDropdown && searchTerm && filteredIngredients.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-4">
                    <div className="text-center text-gray-500">
                      <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">No se encontraron insumos</p>
                      <p className="text-xs text-gray-400">Prueba con otro término de búsqueda</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Selected ingredient display (non-cajero only) */}
            {!isCajero && selectedIngredient && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                <Package className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">{selectedIngredient.name}</span>
                <Badge variant="outline" className="text-xs">
                  {selectedIngredient.unit}
                </Badge>
              </div>
            )}

            {errors.ingredientId && (
              <p className="text-sm text-red-600">{errors.ingredientId}</p>
            )}
          </div>

          {/* Location Selection */}
          <div className="space-y-2">
            <Label htmlFor="location">Sucursal *</Label>
            {isCajero ? (
              // Para CAJERO: mostrar su ubicación fija sin posibilidad de cambiar
              <div className="flex items-center gap-2 p-3 bg-gray-100 border border-gray-300 rounded-md">
                <MapPin className="w-4 h-4 text-gray-600" />
                <span className="text-gray-900 font-medium">
                  {selectedLocation?.name || user?.locationName || 'Ubicación asignada'}
                </span>
              </div>
            ) : (
              // Para otros usuarios: selector normal
              <Select
                value={formData.locationId}
                onValueChange={(value) => setFormData({...formData, locationId: value})}
              >
                <SelectTrigger className={errors.locationId ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Seleccione una sucursal" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.locationId && (
              <p className="text-sm text-red-600">{errors.locationId}</p>
            )}
          </div>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Quantity */}
            <div className="space-y-2">
              <Label htmlFor="quantity">
                Cantidad * {selectedIngredient && `(${selectedIngredient.unit})`}
              </Label>
              <Input
                id="quantity"
                type={isCajero ? "text" : "number"}
                value={formData.quantity > 0 ? formData.quantity.toString() : ''}
                onChange={isCajero ? undefined : (e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                onClick={isCajero ? () => setQuantityKeypadOpen(true) : undefined}
                readOnly={isCajero}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={`${isCajero ? 'cursor-pointer bg-white hover:bg-gray-50' : ''} transition-colors ${errors.quantity ? 'border-red-500' : ''}`}
              />
              {errors.quantity && (
                <p className="text-sm text-red-600">{errors.quantity}</p>
              )}
            </div>

            {/* Movement Type */}
            <div className="space-y-2">
              <Label htmlFor="movementType">
                Tipo de {formData.direction === 'entry' ? 'Ingreso' : 'Salida'} *
              </Label>
              <Select 
                value={formData.movementType}
                onValueChange={(value: MerchandiseMovementType) => setFormData({...formData, movementType: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMovementTypeOptions().map(option => (
                    <SelectItem key={option.id} value={option.id}>
                      <div>
                        <div className="font-medium">{option.name}</div>
                        <div className="text-xs text-gray-500">
                          {option.requiresApproval ? 'Requiere aprobación' : 'Aprobación automática'}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reason (optional) */}
          <div className="space-y-2">
            <Label htmlFor="reason">Razón</Label>
            <Input
              id="reason"
              type="text"
              value={formData.reason}
              onChange={isCajero ? undefined : (e) => setFormData({...formData, reason: e.target.value})}
              onClick={isCajero ? () => setReasonKeypadOpen(true) : undefined}
              readOnly={isCajero}
              placeholder="Opcional"
              className={isCajero ? 'cursor-pointer bg-white hover:bg-gray-50' : ''}
            />
          </div>


          {/* Approval Warning */}
          {willRequireApproval && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-900">Requerirá Aprobación</span>
              </div>
              <p className="text-sm text-amber-800">
                Este {formData.direction === 'entry' ? 'ingreso' : 'salida'} requerirá aprobación debido a:
              </p>
              <ul className="text-sm text-amber-800 mt-1 list-disc list-inside">
                <li>Tipo de movimiento que requiere aprobación</li>
                <li>Valor superior a S/ {APPROVAL_THRESHOLD_AMOUNT.toLocaleString('es-PE')}</li>
                {formData.direction === 'exit' && (
                  <li>Cantidad superior al {APPROVAL_THRESHOLD_PERCENTAGE}% del stock</li>
                )}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            className={formData.direction === 'entry' ? 'bg-green-600 hover:bg-green-700' : 'bg-rose-600 hover:bg-rose-700'}
          >
            Registrar {formData.direction === 'entry' ? 'Ingreso' : 'Salida'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Keypads - Solo para CAJERO (pantalla táctil) */}
      {isCajero && (
        <>
          <TextKeypad
            isOpen={searchKeypadOpen}
            onClose={() => setSearchKeypadOpen(false)}
            onSubmit={(value) => setSearchTerm(value)}
            title="Filtrar insumos"
            initialValue={searchTerm}
            placeholder="NOMBRE DEL INSUMO..."
          />
          <DecimalKeypad
            isOpen={quantityKeypadOpen}
            onClose={() => setQuantityKeypadOpen(false)}
            onSubmit={handleQuantityKeypadSubmit}
            title={`Cantidad ${selectedIngredient ? `(${selectedIngredient.unit})` : ''}`}
            initialValue={formData.quantity.toString()}
          />
          <TextKeypad
            isOpen={reasonKeypadOpen}
            onClose={() => setReasonKeypadOpen(false)}
            onSubmit={handleReasonKeypadSubmit}
            title="Razón del movimiento"
            initialValue={formData.reason}
            placeholder="MOTIVO..."
          />
        </>
      )}
    </Dialog>
  );
};